import React, { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { CheckCircle2, ArrowRight, RotateCcw } from 'lucide-react';

/**
 * Test de Dependencia DAST-10 embebido en modal.
 * Fuente: escala DAST-10 (Drug Abuse Screening Test).
 * Reutilizado desde /herramientas/test-dependencia sin abandonar la landing.
 */
const QUESTIONS = [
  { id: 1, text: '¿Ha usado drogas que no eran requeridas por razones médicas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 2, text: '¿Usted abusa de más de una droga a la vez?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 3, text: '¿Es usted capaz de parar de usar drogas siempre cuando se lo propone?', options: [{ value: 0, label: 'Sí' }, { value: 1, label: 'No' }] },
  { id: 4, text: '¿Ha tenido "pérdidas de conocimiento" o "memoria repentina" como resultado del uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 5, text: '¿Alguna vez se ha sentido mal o culpable debido a su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 6, text: '¿Alguna vez su pareja o familiares se han quejado de su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 7, text: '¿Ha desatendido a su familia debido a su uso de drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 8, text: '¿Se ha implicado en actividades ilegales para obtener drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 9, text: '¿Alguna vez ha experimentado síntomas de abstinencia (sentirse enfermo) al dejar las drogas?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
  { id: 10, text: '¿Ha tenido problemas médicos por uso de drogas (pérdida memoria, hepatitis, etc.)?', options: [{ value: 0, label: 'No' }, { value: 1, label: 'Sí' }] },
];

function getResult(score) {
  if (score === 0) return { title: 'Sin Problema Detectado', color: 'emerald', description: 'No se detectan problemas relacionados con el uso de sustancias.', recommendsProgram: false };
  if (score <= 2) return { title: 'Nivel Bajo', color: 'yellow', description: 'Existe un nivel bajo de problemas relacionados con drogas. Monitorea tu consumo y considera orientación preventiva.', recommendsProgram: false };
  if (score <= 5) return { title: 'Nivel Moderado', color: 'orange', description: 'Nivel moderado de problemas. Un programa de tratamiento ambulatorio y online puede ser adecuado para tu situación.', recommendsProgram: true };
  if (score <= 8) return { title: 'Nivel Sustancial', color: 'red', description: 'Nivel sustancial de problemas. Es importante buscar tratamiento profesional. Nuestro programa puede ayudarte si tu situación permite un tratamiento ambulatorio.', recommendsProgram: true };
  return { title: 'Nivel Severo', color: 'red', description: 'Nivel severo de dependencia. Te recomendamos buscar ayuda profesional urgentemente. Antes de un programa online, es importante evaluar si necesitas atención residencial o desintoxicación supervisada.', recommendsProgram: true, severe: true };
}

const COLOR_STYLES = {
  emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  yellow: 'bg-amber-50 border-amber-200 text-amber-800',
  orange: 'bg-orange-50 border-orange-200 text-orange-800',
  red: 'bg-red-50 border-red-200 text-red-800',
};

export default function DependenciaTestModal({ open, onClose, onCtaClick }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);

  const reset = () => {
    setStep(0);
    setAnswers({});
    setShowResult(false);
  };

  const handleClose = () => {
    reset();
    onClose?.();
  };

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [QUESTIONS[step].id]: value };
    setAnswers(newAnswers);
    if (step < QUESTIONS.length - 1) {
      setTimeout(() => setStep(step + 1), 200);
    } else {
      setTimeout(() => setShowResult(true), 200);
    }
  };

  const score = Object.values(answers).reduce((a, b) => a + b, 0);
  const result = showResult ? getResult(score) : null;
  const progress = ((step + (showResult ? 1 : 0)) / QUESTIONS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg bg-white text-[#1a2942] font-serif p-0 overflow-hidden" data-testid="dependencia-modal">
        {!showResult ? (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-[#e6e2d5]">
              <DialogTitle className="text-lg font-normal">Test de Dependencia (DAST-10)</DialogTitle>
              <p className="text-xs text-[#8896b3] mt-1 font-sans">3 minutos · Confidencial · Basado en escala clínica</p>
              <div className="mt-3 h-1 bg-[#f4f0e6] rounded-full overflow-hidden">
                <div className="h-full bg-[#3b6bb1] transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-[#5a6b8c] mt-2 font-sans">Pregunta {step + 1} de {QUESTIONS.length}</p>
            </div>

            <div className="px-6 py-6" data-testid={`question-${QUESTIONS[step].id}`}>
              <p className="text-lg leading-snug mb-6 min-h-[80px]">{QUESTIONS[step].text}</p>
              <div className="space-y-2 font-sans">
                {QUESTIONS[step].options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAnswer(opt.value)}
                    className="w-full text-left px-4 py-3 border border-[#d4c9a8] rounded-lg hover:bg-[#f4f0e6] hover:border-[#3b6bb1] transition-colors flex items-center justify-between group"
                    data-testid={`answer-${opt.value}`}
                  >
                    <span className="text-[#2a3652] font-medium">{opt.label}</span>
                    <ArrowRight className="w-4 h-4 text-[#8896b3] group-hover:text-[#3b6bb1]" />
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="mt-4 text-xs text-[#8896b3] hover:text-[#1a2942] font-sans"
                  data-testid="back-btn"
                >
                  ← Volver
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="px-6 py-8" data-testid="test-result">
            <div className={`rounded-xl border p-5 mb-5 ${COLOR_STYLES[result.color]}`}>
              <p className="uppercase text-xs tracking-widest font-sans font-semibold mb-1">Resultado</p>
              <h3 className="text-2xl font-normal mb-2">{result.title}</h3>
              <p className="text-sm leading-relaxed font-sans">{result.description}</p>
              <p className="text-xs mt-3 opacity-75 font-sans">Tu puntaje: {score} / 10</p>
            </div>

            {result.recommendsProgram && (
              <div className="bg-[#fdf6ec] border border-[#e0cea2] rounded-lg p-4 mb-4">
                <p className="text-sm text-[#7a5820] leading-relaxed font-sans">
                  {result.severe
                    ? 'Debido al nivel detectado, antes de iniciar un programa online recomendamos una evaluación individual para determinar si tu situación es compatible con tratamiento ambulatorio.'
                    : 'Nuestro Programa Online podría ser adecuado para tu situación. Incluye psicólogo, terapeuta especialista, terapia grupal, seguimiento 24/7 y trabajo con la familia.'}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-2 font-sans">
              {result.recommendsProgram && (
                <Button
                  onClick={onCtaClick}
                  className="w-full bg-[#1a2942] hover:bg-[#2a3652] text-white py-5 rounded-full font-semibold"
                  data-testid="result-cta-form"
                >
                  Solicitar evaluación inicial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
              <button
                onClick={reset}
                className="text-sm text-[#5a6b8c] hover:text-[#1a2942] py-2 flex items-center justify-center gap-1"
                data-testid="reset-btn"
              >
                <RotateCcw className="w-3 h-3" /> Hacer test nuevamente
              </button>
            </div>

            <p className="text-[10px] text-[#8896b3] text-center mt-5 font-sans">
              Este test es orientativo y no reemplaza una evaluación clínica profesional.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
